import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import baseUrl from "../../api/baseUrl";
import { showSmsResultToast, notifySmsBalanceChanged } from "../../utils/sms/smsToast";
import image from "../../assets/patient/Patient card and tests.png";
import { useDispatch } from "react-redux";
import { setloadeDeactive } from "../../redux/loader/loaderActions";
import PhoneInput, { getCountries } from "react-phone-number-input";
import en from "react-phone-number-input/locale/en.json";
import "react-phone-number-input/style.css";
import socket from "../../api/socket";
import { patienttitles } from "../../utils/title";
import { Circles } from "react-loader-spinner";
import PersonIcon from "@mui/icons-material/Person";
import WcIcon from "@mui/icons-material/Wc";
import PhoneIcon from "@mui/icons-material/Phone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PublicIcon from "@mui/icons-material/Public";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import BirthdayWishOptIn from "../../components/sms/BirthdayWishOptIn";

const countryOptions = [
  { code: "LK", name: "Sri Lanka" },
  ...getCountries()
    .filter((code) => code !== "LK")
    .map((code) => ({ code, name: (en as Record<string, string>)[code] || code }))
    .sort((a, b) => a.name.localeCompare(b.name)),
];

function QuickAddPatient() {
  const dispatch = useDispatch();
  const [loading, setloading] = useState(false);
  const [formData, setformData] = useState({
    fullName: "",
    gender: "",
    mobileNo: "",
    address: "",
    country: "Sri Lanka",
    title: "",
    dob: "",
    smsBirthdayWish: false,
  });
  const [foundPatients, setFoundPatients] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleChange = (e: any) => {
    if (e.target.type === "number") {
      e.target.value = e.target.value.replace(/e/g, "");
      if (/^\d{0,9}$/.test(e.target.value)) {
        setformData({ ...formData, [e.target.name]: e.target.value });
      }
    } else {
      const next = { ...formData, [e.target.name]: e.target.value };
      if (e.target.name === "dob" && !e.target.value) {
        next.smsBirthdayWish = false;
      }
      setformData(next);
    }
  };

  const findPatientByPhonenumber = useCallback((phonenumber: string) => {
    if (!phonenumber || phonenumber.length < 5) {
      setFoundPatients([]);
      setShowDropdown(false);
      return;
    }

    setSearching(true);
    baseUrl
      .post("client/patient/find-patients-by-phonenumber", { phonenumber })
      .then((response) => {
        console.log(response.data);

        if (response.data.success) {
          setFoundPatients(response.data.data || []);
          setShowDropdown(response.data.data && response.data.data.length > 0);
        } else {
          setFoundPatients([]);
          setShowDropdown(false);
        }
        setSearching(false);
      })
      .catch((error) => {
        console.error("Error finding patients:", error);
        setFoundPatients([]);
        setShowDropdown(false);
        setSearching(false);
      });
  }, []);

  const handlePhonenumber = (e: any) => {
    const phoneNumber = e || "";
    setformData({ ...formData, mobileNo: phoneNumber });

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce API call
    debounceTimerRef.current = setTimeout(() => {
      findPatientByPhonenumber(phoneNumber);
    }, 500);
  };

  const handleSelectPatient = (patient: any) => {
    setformData({
      ...formData,
      fullName: patient.fullName || "",
      gender: patient.gender || "",
      mobileNo: patient.mobileNo || formData.mobileNo,
      address:
        typeof patient.address === "object" && patient.address !== null
          ? patient.address.street || ""
          : patient.address || "",
      country:
        patient.country ||
        (typeof patient.address === "object" && patient.address !== null
          ? patient.address.country
          : "") ||
        "Sri Lanka",
      title: patient.title || "",
      dob: patient.dob ? patient.dob.split("T")[0] : "",
      smsBirthdayWish: Boolean(patient.smsBirthdayWish),
    });
    setShowDropdown(false);
    setFoundPatients([]);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const onSubmit = (data: any) => {
    data.preventDefault();
    setloading(true);
    baseUrl
      .post("patient/quick-register", formData)
      .then((response) => {
        if (response.data.success) {
          setloading(false);
          toast.success("Patient Registered Successfully");
          showSmsResultToast(response.data.sms);
          notifySmsBalanceChanged();
          socket.emit("addpatients", response.data.data);
          setformData({
            fullName: "",
            gender: "",
            mobileNo: "",
            address: "",
            country: "Sri Lanka",
            title: "",
            dob: "",
            smsBirthdayWish: false,
          });
        }
      })
      .catch((error) => {
        dispatch(setloadeDeactive());
        setloading(false);
      });

    return () => {
      socket.disconnect();
    };
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-600">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/40 rounded-lg flex items-center justify-center">
            <PersonAddIcon sx={{ fontSize: 18 }} className="text-indigo-600 dark:text-indigo-300" />
          </div>
          <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Quick Add Patient</h2>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row">
        {/* Form Section */}
        <div className="flex-1 p-4 relative overflow-visible">
          <form onSubmit={onSubmit} className="space-y-3.5">
            {/* Contact Number */}
            <div className="space-y-1.5 relative z-10" ref={dropdownRef}>
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 dark:text-slate-300">
                <PhoneIcon sx={{ fontSize: 16 }} className="text-indigo-500" />
                Contact Number
              </label>
              <div className="[&_.PhoneInput]:h-10 [&_.PhoneInput]:flex [&_.PhoneInput]:items-center [&_.PhoneInput]:bg-slate-50 dark:[&_.PhoneInput]:bg-slate-700 [&_.PhoneInput]:border [&_.PhoneInput]:border-gray-600 dark:[&_.PhoneInput]:border-slate-600 [&_.PhoneInput]:rounded-lg [&_.PhoneInput]:px-3 [&_.PhoneInput]:text-sm [&_.PhoneInputInput]:h-full [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:border-none [&_.PhoneInputInput]:text-sm [&_.PhoneInputInput]:text-slate-800 dark:[&_.PhoneInputInput]:text-slate-200">
                <PhoneInput
                  international
                  countryCallingCodeEditable={false}
                  defaultCountry="LK"
                  value={formData.mobileNo}
                  onChange={handlePhonenumber}
                />
              </div>
              {/* Patient Dropdown */}
              {showDropdown && (
                <div className="absolute z-[100] w-full mt-1.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg shadow-xl shadow-slate-300/30 dark:shadow-slate-900/50 max-h-48 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-slate-100 dark:[&::-webkit-scrollbar-track]:bg-slate-700 [&::-webkit-scrollbar-thumb]:bg-slate-300 dark:[&::-webkit-scrollbar-thumb]:bg-slate-500 [&::-webkit-scrollbar-thumb]:rounded-full">
                  {searching ? (
                    <div className="p-4 text-center text-slate-500 dark:text-slate-400 text-xs flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                      <span>Searching...</span>
                    </div>
                  ) : foundPatients.length > 0 ? (
                    <div className="py-1">
                      {foundPatients.map((patient: any, index: number) => (
                        <div
                          key={patient._id || index}
                          onClick={() => handleSelectPatient(patient)}
                          className="px-3 py-2.5 hover:bg-indigo-50 dark:hover:bg-slate-700/80 cursor-pointer transition-all duration-200 border-b border-slate-100 dark:border-slate-600 last:border-b-0 active:bg-indigo-100 dark:active:bg-slate-600"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center text-white font-semibold text-xs shadow-sm">
                              {patient.fullName?.charAt(0)?.toUpperCase() ||
                                "P"}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">
                                {patient.fullName || "N/A"}
                              </p>
                              <div className="flex items-center gap-2.5 mt-1 flex-wrap">
                                {patient.regNo && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-600 text-[10px] font-medium text-slate-600 dark:text-slate-300">
                                    Reg: {patient.regNo}
                                  </span>
                                )}
                                {patient.gender && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-indigo-100 dark:bg-indigo-900/40 text-[10px] font-medium text-indigo-700 dark:text-indigo-300 capitalize">
                                    {patient.gender}
                                  </span>
                                )}
                                {patient.mobileNo && (
                                  <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">
                                    {patient.mobileNo}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-slate-500 dark:text-slate-400 text-xs">
                      <p className="font-medium">No patients found</p>
                      <p className="text-[10px] mt-1 text-slate-400 dark:text-slate-500">Try a different phone number</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Full Name */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 dark:text-slate-300">
                <PersonIcon sx={{ fontSize: 16 }} className="text-indigo-500" />
                Full Name <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <select
                  name="title"
                  onChange={handleChange}
                  value={formData.title}
                  required={true}
                  className="h-10 px-3 bg-slate-50 dark:bg-slate-700 border border-gray-600 dark:border-slate-600 rounded-lg outline-none transition-all text-xs text-slate-700 dark:text-slate-200"
                >
                  <option value="">Title</option>
                  {patienttitles.map((item: any, i: number) => (
                    <option key={i} value={item.value}>
                      {item.title}
                    </option>
                  ))}
                </select>
                <input
                  name="fullName"
                  required={true}
                  type="text"
                  placeholder="Enter Full Name"
                  onChange={handleChange}
                  value={formData.fullName}
                  className="flex-1 h-10 px-3 bg-slate-50 dark:bg-slate-700 border border-gray-600 dark:border-slate-600 rounded-lg outline-none transition-all text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                />
              </div>
            </div>
            {/* Gender */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 dark:text-slate-300">
                <WcIcon sx={{ fontSize: 16 }} className="text-indigo-500" />
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                id="dropdown"
                name="gender"
                required={true}
                onChange={handleChange}
                value={formData.gender}
                className="w-full h-10 px-3 bg-slate-50 dark:bg-slate-700 border border-gray-600 dark:border-slate-600 rounded-lg outline-none transition-all text-xs text-slate-700 dark:text-slate-200"
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            {/* Date of Birth */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 dark:text-slate-300">
                <CalendarTodayIcon
                  sx={{ fontSize: 16 }}
                  className="text-indigo-500"
                />
                Date of Birth
              </label>
              <input
                placeholder="DOB"
                type="date"
                name="dob"
                onChange={handleChange}
                value={formData.dob}
                className="w-full h-10 px-3 bg-slate-50 dark:bg-slate-700 border border-gray-600 dark:border-slate-600 rounded-lg outline-none transition-all text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
              />
            </div>
            <BirthdayWishOptIn
              dob={formData.dob}
              checked={Boolean(formData.smsBirthdayWish)}
              onChange={(value) => setformData({ ...formData, smsBirthdayWish: value })}
            />
            {/* Address */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 dark:text-slate-300">
                <LocationOnIcon
                  sx={{ fontSize: 16 }}
                  className="text-indigo-500"
                />
                Address
              </label>
              <input
                placeholder="Enter Address"
                type="text"
                name="address"
                onChange={handleChange}
                value={formData.address}
                className="w-full h-10 px-3 bg-slate-50 dark:bg-slate-700 border border-gray-600 dark:border-slate-600 rounded-lg outline-none transition-all text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
              />
            </div>
            {/* Country */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 dark:text-slate-300">
                <PublicIcon sx={{ fontSize: 16 }} className="text-indigo-500" />
                Country
              </label>
              <select
                name="country"
                onChange={handleChange}
                value={formData.country}
                className="w-full h-10 px-3 bg-slate-50 dark:bg-slate-700 border border-gray-600 dark:border-slate-600 rounded-lg outline-none transition-all text-xs text-slate-700 dark:text-slate-200"
              >
                {countryOptions.map((item) => (
                  <option key={item.code} value={item.name}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
            {/* Buttons */}
            <div className="flex gap-2 pt-3">
              <button
                type="button"
                className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-lg font-medium text-xs hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg font-medium text-xs hover:shadow-lg hover:shadow-primary/30 transition-all flex items-center justify-center gap-1.5"
              >
                <PersonAddIcon sx={{ fontSize: 16 }} />
                Add Patient
              </button>
            </div>
          </form>
        </div>
        {/* Image Section */}
        <div className="hidden lg:flex lg:w-[400px] bg-white dark:bg-slate-800/50 items-center justify-center p-4">
          <img src={image} alt="add-patients" className="max-w-full h-auto" />
        </div>
      </div>
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Circles height="60" width="60" color="#6366f1" ariaLabel="loading" />
        </div>
      )}
    </div>
  );
}

export default QuickAddPatient;
